/**
 * Utilitários para mensagens de erro amigáveis ao usuário final
 * 
 * Converte erros técnicos em mensagens simples e acionáveis.
 * 
 * ✅ MELHORADO: Integra com handleToolsApiError quando disponível para melhor
 * tratamento de erros da API Tools.
 */

import { getSafeErrorMessage } from '../config';
import { APIError } from './api';
import { AppError } from './errors';
import { handleToolsApiError } from './toolsErrorHandler';
import { sendToTerminal, logger } from './terminalLogger';
import { LogCategory } from '../config/logging';

/**
 * Mapeia códigos de erro comuns para mensagens amigáveis
 */
const ERROR_MESSAGES: Record<string, { title: string; message: string; hint?: string }> = {
  // Erros de autenticação
  '401': {
    title: 'Sessão Expirada',
    message: 'Sua sessão expirou. Por favor, faça login novamente.',
    hint: 'Feche esta janela e abra as Ferramentas novamente pelo menu principal.'
  },
  '403': {
    title: 'Acesso Negado',
    message: 'Você não tem permissão para realizar esta ação.',
    hint: 'Entre em contato com o administrador se precisar deste acesso.'
  },
  
  // Erros de conexão
  'network': {
    title: 'Problema de Conexão',
    message: 'Não foi possível conectar ao servidor.',
    hint: 'Verifique sua conexão com a internet e tente novamente.'
  },
  'timeout': {
    title: 'Tempo Esgotado',
    message: 'A operação demorou mais que o esperado.',
    hint: 'O servidor pode estar ocupado. Tente novamente em alguns instantes.'
  },
  
  // Erros de carregamento
  'shortcuts_load_failed': {
    title: 'Atalhos Não Carregados',
    message: 'Não foi possível carregar os atalhos de teclado.',
    hint: 'As ferramentas continuam funcionando. Você pode usar os botões na tela.'
  },
  'permissions_load_failed': {
    title: 'Permissões Não Carregadas',
    message: 'Não foi possível carregar suas permissões.',
    hint: 'Algumas funcionalidades podem estar limitadas. Recarregue a página se necessário.'
  },
  
  // Erros de ferramentas
  'tool_execution_failed': {
    title: 'Ferramenta Não Executada',
    message: 'Não foi possível executar a ferramenta.',
    hint: 'Verifique se o CorelDRAW está aberto e respondendo. Tente novamente.'
  },
  'addon_outdated': {
    title: 'Add-on Desatualizado',
    message: 'A versão do add-on instalada está desatualizada.',
    hint: 'Atualize o add-on e reinicie o CorelDRAW para usar esta funcionalidade.'
  },
  
  // Erros de launcher
  'LAUNCHER_NOT_READY': {
    title: 'Sistema Inicializando',
    message: 'O sistema está inicializando. Aguarde alguns segundos e tente novamente.',
    hint: '💡 O Launcher está carregando. Espere alguns segundos e repita a ação.'
  },
  
  // Erros genéricos
  'unknown': {
    title: 'Ocorreu um Erro',
    message: 'Algo inesperado aconteceu.',
    hint: 'Tente novamente. Se o problema persistir, recarregue a página.'
  }
};

/**
 * Detecta o tipo de erro e retorna mensagem amigável
 */
function detectErrorType(error: any): string {
  if (!error) {
    logger.warn('[USER_FRIENDLY_ERRORS] detectErrorType recebeu erro null/undefined');
    return 'unknown';
  }
  
  const errorStr = String(error).toLowerCase();
  const errorMessage = error instanceof Error ? error.message.toLowerCase() : errorStr;
  const status = error?.status || error?.response?.status;
  
  // Erros HTTP
  if (status === 401) return '401';
  if (status === 403) return '403';
  if (status === 408 || status === 504) return 'timeout';
  
  // Erros de rede
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || 
      errorMessage.includes('connection') || errorMessage.includes('conexão')) {
    return 'network';
  }
  
  if (errorMessage.includes('timeout') || errorMessage.includes('tempo')) {
    return 'timeout';
  }
  
  // Erros específicos
  if (errorMessage.includes('atalho') || errorMessage.includes('shortcut')) {
    return 'shortcuts_load_failed';
  }
  
  if (errorMessage.includes('permissão') || errorMessage.includes('permission')) {
    return 'permissions_load_failed';
  }
  
  if (errorMessage.includes('add-on') || errorMessage.includes('addon')) {
    return 'addon_outdated';
  }
  
  // ✅ NOVO: Erro de launcher não pronto
  if (errorMessage.includes('launcher_session_required') || 
      errorMessage.includes('booting') ||
      error?.code === 'launcher_session_required' ||
      error?.result?.launcherState === 'booting') {
    return 'LAUNCHER_NOT_READY';
  }
  
  // ✅ DEBUG: Log de erro não reconhecido para investigação
  logger.warn('[USER_FRIENDLY_ERRORS] Tipo de erro não reconhecido, usando fallback', {
    errorType: typeof error,
    errorStr: errorStr.substring(0, 200),
    errorMessage: errorMessage?.substring(0, 200),
    status,
    hasMessage: !!error?.message,
    hasStatus: !!status,
    constructorName: error?.constructor?.name,
    keys: Object.keys(error || {}).slice(0, 10)
  });
  
  return 'unknown';
}

/**
 * Converte erro técnico em mensagem amigável para o usuário
 * 
 * ✅ MELHORADO: Integra com handleToolsApiError quando disponível
 * 
 * @param error - Erro original (pode ser Error, string, ou objeto com status)
 * @param context - Contexto adicional (ex: 'carregar atalhos', 'executar ferramenta')
 * @returns Objeto com title, message e hint amigáveis
 */
export function getUserFriendlyError(
  error: any,
  context?: string
): { title: string; message: string; hint?: string } {
  // ✅ DEBUG: Log de entrada para rastreamento
  logger.debug('[USER_FRIENDLY_ERRORS] Processando erro', {
    context,
    errorType: typeof error,
    isAPIError: error instanceof APIError,
    isAppError: error instanceof AppError,
    hasMessage: !!error?.message,
    hasStatus: !!error?.status,
    errorConstructor: error?.constructor?.name
  });
  
  // ✅ MELHORADO: Se for APIError, tentar usar handleToolsApiError primeiro
  if (error instanceof APIError) {
    const appError = handleToolsApiError(error, context as any);
    
    // Se o AppError tem título e mensagem do ErrorCatalog, usar eles
    if (appError.title && appError.message) {
      return {
        title: appError.title,
        message: appError.message,
        hint: appError.details
      };
    }
  }
  
  // ✅ MELHORADO: Se for AppError, usar diretamente
  if (error instanceof AppError) {
    // ✅ NOVO: Verificar se é erro de launcher não pronto
    if (error.code === 'LAUNCHER_NOT_READY') {
      return {
        title: error.title || 'Sistema Inicializando',
        message: error.message || 'O sistema está inicializando. Aguarde alguns segundos e tente novamente.',
        hint: '💡 O Launcher está carregando. Espere alguns segundos e repita a ação.'
      };
    }
    
    return {
      title: error.title || 'Erro',
      message: error.message,
      hint: error.details
    };
  }
  
  // Fallback: usar detecção padrão
  const errorType = detectErrorType(error);
  const template = ERROR_MESSAGES[errorType] || ERROR_MESSAGES['unknown'];
  
  // ✅ DEBUG: Log quando cair no fallback 'unknown'
  if (errorType === 'unknown') {
    logger.error('[USER_FRIENDLY_ERRORS] Erro mapeado para tipo unknown', {
      context: context || 'não especificado',
      errorType: typeof error,
      errorMessage: error?.message || 'sem mensagem',
      errorStack: error?.stack?.substring(0, 500) || 'sem stack',
      errorKeys: Object.keys(error || {}),
      errorString: String(error).substring(0, 200)
    }, true); // critical: true para garantir log
  }
  
  // Personalizar mensagem com contexto se fornecido
  let message = template.message;
  if (context) {
    message = message.replace('a operação', `a ação "${context}"`);
    message = message.replace('A operação', `A ação "${context}"`);
  }
  
  return {
    title: template.title,
    message,
    hint: template.hint
  };
}

/**
 * Formata mensagem completa com hint (se disponível)
 */
export function formatErrorMessage(
  error: any,
  context?: string
): string {
  const friendly = getUserFriendlyError(error, context);
  
  if (friendly.hint) {
    return `${friendly.message}\n\n💡 ${friendly.hint}`;
  }
  
  return friendly.message;
}

/**
 * Helper para notificar erro de forma amigável
 * 
 * @example
 * notifyUserFriendlyError(error, notify, 'carregar atalhos');
 */
export function notifyUserFriendlyError(
  error: any,
  notifyFn: (params: any) => string,
  context?: string,
  options?: {
    sticky?: boolean;
    duration?: number;
    actions?: Array<{ label: string; onClick: () => void }>;
  }
): string {
  // ✅ DEBUG: Log antes de notificar usuário
  logger.info('[USER_FRIENDLY_ERRORS] Notificando erro ao usuário', {
    context: context || 'não especificado',
    errorType: typeof error,
    errorMessage: error?.message || 'sem mensagem',
    errorStatus: error?.status || error?.response?.status,
    options
  });
  
  const friendly = getUserFriendlyError(error, context);
  const fullMessage = friendly.hint 
    ? `${friendly.message}\n\n💡 ${friendly.hint}`
    : friendly.message;
  
  // ✅ DEBUG: Log da mensagem final que será exibida
  logger.info('[USER_FRIENDLY_ERRORS] Mensagem exibida ao usuário', {
    context: context || 'não especificado',
    title: friendly.title,
    message: friendly.message,
    hint: friendly.hint
  });
  
  return notifyFn({
    type: 'error',
    title: friendly.title,
    message: fullMessage,
    sticky: options?.sticky ?? false,
    duration: options?.duration ?? 10000,
    actions: options?.actions
  });
}

